import type { Position, Trigger } from "@/types/workflowDefinition";
import type {
	Action,
	WorkflowDefinition,
} from "@/types/workflowDefinition";
import {
	isIfAction,
	isSwitchAction,
	isForeachAction,
	isUntilAction,
	isAgentAction,
} from "@/types/workflowDefinition";
import type { OperationDefinition } from "@/types/operations";
import { serializeHost, applyHostSerialization } from "@/services/hostSerializer";
import type { AvailableConnection, ConnectionReference, ConnectionReferences, ReferenceKey } from "@/types/connectionReference";
import {
	NODE_SPACING,
	DEFAULT_NODE_WIDTH,
	DEFAULT_NODE_HEIGHT,
	SNAP_GRID,
	DEFAULT_TRIGGER_POSITION,
} from "@/utils/workflow";
import {
	isAgentConnectionId,
	isManagedApiConnector,
	isServiceProviderConnector,
	referenceHasConnection,
	getLastUsedAgentConnection,
	type AgentModelProvider,
	normalizeProviderName,
	FOUNDRY_AGENT_PROVIDERS,
	NATIVE_AGENT_PROVIDERS,
	CODING_AGENT_PROVIDERS,
	toBackendAgentConnectionType,
} from "@/utils/connections";
import type { AgentVariant } from "./helpers/agentVariant";
import { getAgentVariantFromTypes } from "./helpers/agentVariant";

// Re-exported from `@/utils/agentConnectionType` so existing UI imports of these
// symbols from this module continue to work. New code in non-UI layers (stores,
// services) should import directly from `@/utils/agentConnectionType` to avoid
// taking a dependency on the WorkflowDesigner component tree.
export {
	type AgentModelProvider,
	FOUNDRY_AGENT_PROVIDERS,
	NATIVE_AGENT_PROVIDERS,
	CODING_AGENT_PROVIDERS,
	toBackendAgentConnectionType,
};

/**
 * Check if a trigger is an agent trigger (A2A - Agent-to-Agent communication).
 * Agent triggers have type: 'Request' and kind: 'Agent'.
 * When an agent trigger is added, the workflow kind should be set to 'Agent'.
 */
export function isAgentTrigger(trigger: Trigger): boolean {
	return trigger.type === 'Request' && trigger.kind === 'Agent';
}

/**
 * Check if a node/trigger type is a Request-class trigger.
 *
 * Both the standard HTTP Request trigger ("When an HTTP request is received")
 * and the A2ARequest agent trigger share `type: 'Request'` in the workflow
 * definition. Callers that need runtime URL hydration via /listCallbackUrl
 * (which exists for any Request-typed trigger) should gate on this helper
 * instead of duplicating the literal string check.
 *
 * Returns false for `undefined`/`null` to make it safe to call with optional
 * `node?.nodeData?.type` expressions.
 */
export function isRequestTriggerType(type: string | undefined | null): boolean {
	return type === 'Request';
}

/**
 * Find ALL existing connection references that match the given connector ID.
 * Returns all matching connections, allowing the UI to present options when multiple exist.
 *
 * @param connectorId - The connector ID to match (e.g., "/managedApis/sharepointonline" or "/serviceProviders/AzureBlob")
 * @param references - The connection references from the workflow
 * @returns Array of reference keys for all matching connections
 */
export const findAllMatchingConnectionReferences = (
	connectorId: string | undefined,
	connections: AvailableConnection[],
	references: ConnectionReferences | undefined,
): ReferenceKey[] => {
	if (!connectorId || !references) return [];

	const matches: ReferenceKey[] = [];
	const normalizedConnectorId = connectorId.toLowerCase();

	// Extract API name for flexible matching
	const managedApiMatch = isManagedApiConnector(connectorId) ? connectorId.match(/\/managedApis\/([^/]+)$/i) : null;
	const apiName = managedApiMatch?.[1]?.toLowerCase();

	// Extract service provider name for flexible matching
	const serviceProviderMatch = isServiceProviderConnector(connectorId) ? connectorId.match(/\/serviceProviders\/([^/]+)/i) : null;
	const providerName = serviceProviderMatch?.[1]?.toLowerCase();

	for (const [refKey, ref] of Object.entries(references)) {
		const refApiId = ref.api.id.toLowerCase();

		// Exact match
		if (refApiId === normalizedConnectorId) {
			matches.push(refKey);
			continue;
		}

		// Managed API partial match (different subscription paths but same API)
		if (apiName && refApiId.includes(`/managedapis/${apiName}`)) {
			matches.push(refKey);
			continue;
		}

		// Service provider match
		if (providerName && refApiId.includes(`/serviceproviders/${providerName}`)) {
			matches.push(refKey);
			continue;
		}
	}

	return matches.filter(refKey => referenceHasConnection(references[refKey] as ConnectionReference, connections));
};

/**
 * Find the first existing connection reference that matches the given connector ID.
 * Convenience wrapper for findAllMatchingConnectionReferences when only one is needed.
 *
 * @param connectorId - The connector ID to match
 * @param references - The connection references from the workflow
 * @returns The first matching reference key, or undefined if none found
 */
export const findMatchingConnectionReference = (
	connectorId: string | undefined,
	connections: AvailableConnection[],
	references: ConnectionReferences | undefined,
): ReferenceKey | undefined => {
	const matches = findAllMatchingConnectionReferences(connectorId, connections, references);
	return matches.length > 0 ? matches[0] : undefined;
};

/**
 * Infer the model provider (AzureOpenAI / OpenAI / FoundryAgentService[V2]) for a
 * connection reference, preferring an explicit `connectionProperties.type` over
 * pattern-matching the `api.id`.
 *
 * Legacy connections written before the provider name was persisted into
 * `connectionProperties.type` carry only a generic `'model'` marker. For
 * those we fall through to `resourceId` (a Cognitive Services account id
 * identifies Azure OpenAI) and `endpoint` (e.g. `api.openai.com` identifies
 * OpenAI) before consulting `api.id`.
 */
export const getAgentReferenceProvider = (ref: ConnectionReference | undefined): AgentModelProvider | undefined => {
	if (!ref) return undefined;
	const props = ref.connectionProperties as Record<string, unknown> | undefined;
	const explicit = props?.type;
	if (typeof explicit === 'string') {
		const fromType = normalizeProviderName(explicit);
		if (fromType) return fromType;
	}
	// Foundry-specific signals must beat the generic resourceId/endpoint fallbacks
	// below: both AzureOpenAI and Foundry resources live under Microsoft.CognitiveServices,
	// so a legacy Foundry connection (written with type='model') would otherwise be
	// misclassified as AzureOpenAI by the 'cognitiveservices' substring match. The
	// `/projects/` segment and `projectId` property are unique to Foundry.
	const projectId = props?.projectId;
	if (typeof projectId === 'string' && projectId.length > 0) return 'FoundryAgentService';
	const resourceId = props?.resourceId;
	if (typeof resourceId === 'string' && /\/projects\//i.test(resourceId)) return 'FoundryAgentService';

	const endpoint = props?.endpoint;
	if (typeof endpoint === 'string') {
		const fromEndpoint = normalizeProviderName(endpoint);
		if (fromEndpoint) return fromEndpoint;
	}

	const apiId = ref.api?.id;
	if (typeof apiId === 'string') {
		// Last path segment first (handles `/agentConnections/AzureOpenAI`), then full id.
		const lastSegment = apiId.split('/').filter(Boolean).pop();
		const fromSegment = normalizeProviderName(lastSegment);
		if (fromSegment) return fromSegment;
		const fromFull = normalizeProviderName(apiId);
		if (fromFull) return fromFull;
	}
	return undefined;
};

/**
 * Provider set allowed for an agent variant. Coding agents additionally permit
 * GitHub Copilot connections; workflow (native) agents do not (GHCP is coding-
 * only). Foundry agents are restricted to the Foundry providers.
 */
export const getAllowedAgentProviders = (
	variant: AgentVariant,
): ReadonlySet<AgentModelProvider> =>
	variant === 'foundry'
		? FOUNDRY_AGENT_PROVIDERS
		: variant === 'coding'
			? CODING_AGENT_PROVIDERS
			: NATIVE_AGENT_PROVIDERS;

/**
 * Read the agent's `agentModelType` from the node-editor parameter store, with
 * an optional fallback to the live action `inputs` for freshly-created agents
 * whose parameter store hasn't been hydrated yet.
 *
 * Returns `undefined` when neither source carries a value; callers branching
 * on Foundry-vs-native should pass the result to `isFoundryAgentModelType`,
 * which treats `undefined` as native.
 */
export const readAgentModelType = (
	parameterValues: Record<string, unknown>,
	nodeInputs?: Record<string, unknown>,
): string | undefined => {
	const fromBare = parameterValues['agentModelType'];
	if (typeof fromBare === 'string' && fromBare) return fromBare;
	const fromParams = parameterValues['parameters.agentModelType'];
	if (typeof fromParams === 'string' && fromParams) return fromParams;
	if (nodeInputs) {
		const direct = nodeInputs['agentModelType'];
		if (typeof direct === 'string' && direct) return direct;
		const nested = (nodeInputs['parameters'] as Record<string, unknown> | undefined)?.['agentModelType'];
		if (typeof nested === 'string' && nested) return nested;
	}
	return undefined;
};

/**
 * Read the agent harness type from the node-editor parameter store or
 * the live action inputs. Returns the harness type string (e.g. 'GHCP')
 * or `undefined` when not set (non-coding agents).
 */
export const readAgentHarnessType = (
	parameterValues: Record<string, unknown>,
	nodeInputs?: Record<string, unknown>,
): string | undefined => {
	const extractHarness = (host: unknown): string | undefined => {
		if (!host || typeof host !== 'object' || Array.isArray(host)) return undefined;
		const settings = (host as Record<string, unknown>).agentModelSettings;
		if (!settings || typeof settings !== 'object' || Array.isArray(settings)) return undefined;
		const harness = (settings as Record<string, unknown>).agentHarness;
		if (!harness || typeof harness !== 'object' || Array.isArray(harness)) return undefined;
		const type = (harness as Record<string, unknown>).type;
		return typeof type === 'string' && type ? type : undefined;
	};
	// Check parameterValues (flattened node-editor store)
	const pvParams = parameterValues['parameters'] as Record<string, unknown> | undefined;
	const fromPv = extractHarness(pvParams);
	if (fromPv) return fromPv;
	// Check nodeInputs (live action definition)
	if (nodeInputs) {
		const fromTop = extractHarness(nodeInputs);
		if (fromTop) return fromTop;
		const params = (nodeInputs as Record<string, unknown>).parameters;
		const fromNested = extractHarness(params);
		if (fromNested) return fromNested;
	}
	return undefined;
};

export interface FindAgentConnectionOptions {
	/** Operation's `agentModelType`. Refs with this provider are preferred over others. */
	preferredProvider?: string;
	/** Most recently used reference key (workflow-scoped tiebreaker). */
	lastUsedKey?: ReferenceKey;
	/** Most recently used `connection.id` (cross-workflow tiebreaker). */
	lastUsedConnectionId?: string;
	/**
	 * Providers permitted for the agent variant being configured. When set, a
	 * connection whose provider is *known* and not in this set is excluded from
	 * auto-selection (connections with an unknown provider are still eligible, so
	 * unmanaged/data-plane-only entries remain usable). This keeps the auto-select
	 * fallback consistent with the variant-aware connection picker — e.g. a
	 * workflow (native) agent never auto-binds a GitHub Copilot connection.
	 */
	allowedProviders?: ReadonlySet<AgentModelProvider>;
}

/**
 * Find the connection reference that should back an Agent action's `model1` slot.
 *
 * Selection order:
 *   1. Among refs that match `preferredProvider`, pick the one whose `connection.id`
 *      matches `lastUsedConnectionId`, then whose key matches `lastUsedKey`, then the first.
 *   2. If no provider match, fall back to the same recency-then-first selection over
 *      all agent refs. This keeps users with a single non-matching connection productive
 *      rather than leaving the node unconfigured.
 */
export const findMatchingAgentConnectionReference = (
	connections: AvailableConnection[],
	references: ConnectionReferences | undefined,
	options: FindAgentConnectionOptions = {},
): ReferenceKey | undefined => {
	if (!references) return undefined;
	const { preferredProvider, lastUsedKey, lastUsedConnectionId, allowedProviders } = options;
	const targetProvider = normalizeProviderName(preferredProvider);

	const eligible: Array<{ key: ReferenceKey; ref: ConnectionReference; provider: AgentModelProvider | undefined }> = [];
	for (const [refKey, ref] of Object.entries(references)) {
		if (!ref?.api?.id) continue;
		if (!isAgentConnectionId(ref.api.id)) continue;
		if (!referenceHasConnection(ref, connections)) continue;
		const provider = getAgentReferenceProvider(ref);
		// Exclude connections whose provider is known and not allowed for this
		// variant. Unknown providers stay eligible so unmanaged connections remain
		// usable (mirrors the variant-aware picker's behavior).
		if (allowedProviders && provider !== undefined && !allowedProviders.has(provider)) continue;
		eligible.push({ key: refKey, ref, provider });
	}
	if (eligible.length === 0) return undefined;

	const pickWithRecency = (candidates: typeof eligible): ReferenceKey | undefined => {
		if (candidates.length === 0) return undefined;
		if (lastUsedConnectionId) {
			const byConn = candidates.find((c) => c.ref.connection?.id === lastUsedConnectionId);
			if (byConn) return byConn.key;
		}
		if (lastUsedKey) {
			const byKey = candidates.find((c) => c.key === lastUsedKey);
			if (byKey) return byKey.key;
		}
		return candidates[0]?.key;
	};

	if (targetProvider) {
		const providerMatches = eligible.filter((c) => c.provider === targetProvider);
		const picked = pickWithRecency(providerMatches);
		if (picked) return picked;
	}
	return pickWithRecency(eligible);
};

/**
 * Build inputs for an operation.
 *
 * Following LogicAppsUX pattern:
 * - For most operations: Returns an object with inputs structured for the action.
 * - For Compose-type operations: Returns the value directly (when defaultInputs is a primitive).
 *
 * Sets up host.apiId for Azure managed connectors to enable swagger parameter lookup.
 * Sets up serviceProviderConfiguration for Service Provider operations.
 *
 * If connectionReferences is provided and a matching connection exists,
 * the connection reference is automatically populated.
 */
/** Type for action/trigger inputs - matches workflowDefinition schema */
type ActionInputs = Record<string, unknown> | string | number | boolean | unknown[];

export const buildConnectorInputs = (
	operation: OperationDefinition,
	connections: AvailableConnection[],
	connectionReferences?: ConnectionReferences,
): ActionInputs => {
	// Following LogicAppsUX pattern: defaultInputs can be any value
	// For most operations: object with property defaults
	// For Compose-type operations: direct value (string, number, etc.)
	const defaultInputs = operation.defaultInputs;

	// If defaultInputs is a non-object primitive, return it directly
	// This handles Compose and similar operations where inputs is a direct value
	if (defaultInputs !== null && defaultInputs !== undefined &&
		(typeof defaultInputs !== 'object' || Array.isArray(defaultInputs))) {
		return defaultInputs as ActionInputs;
	}

	// Standard object-based inputs - spread to allow modification
	// Use empty object when defaultInputs is null/undefined (e.g., discovery API operations)
	const inputs = (defaultInputs && typeof defaultInputs === 'object' && !Array.isArray(defaultInputs))
		? { ...defaultInputs } as Record<string, unknown>
		: {} as Record<string, unknown>;

	// If this is a managed connector operation (ApiConnection), set up the host object with apiId
	if (operation.connectorId && operation.type === "ApiConnection") {
		const defaultConnectionRef = findMatchingConnectionReference(
			operation.connectorId,
			connections,
			connectionReferences,
		);
		const serialized = serializeHost({
			connectionType: 'ApiConnection',
			connectionKey: defaultConnectionRef ?? '',
			apiId: operation.connectorId,
			operationId: extractOperationId(operation),
		});
		applyHostSerialization(inputs, serialized, { merge: true });
	}

	// ApiConnectionNotification triggers (subscription-based like Office365 "When a new email arrives")
	if (operation.connectorId && operation.type === "ApiConnectionNotification") {
		const defaultConnectionRef = findMatchingConnectionReference(
			operation.connectorId,
			connections,
			connectionReferences,
		);
		applyHostSerialization(inputs, serializeHost({
			connectionType: 'ApiConnectionNotification',
			connectionKey: defaultConnectionRef ?? '',
			apiId: operation.connectorId,
			operationId: extractOperationId(operation),
		}));

		const rawPath = operation.swaggerPathTemplate || "";
		const subscribePath = rawPath.replace(/^\/{connectionId}/, "");
		inputs.subscribe = {
			body: {
				NotificationUrl: "${listCallbackUrl()}",
			},
			method: "post",
			pathTemplate: {
				template: subscribePath,
			},
		};
		inputs.fetch = {
			method: "get",
			pathTemplate: {
				template: subscribePath,
				parameters: {},
			},
		};
	}

	// ApiConnectionWebhook triggers and actions (callback-based).
	// Serialized identically to ApiConnection: flat host + path + body. The
	// path/method/body are produced on save by buildSwaggerInputsFromDefinitions
	// from the swagger operation; the body's `notificationUrl` is auto-filled
	// from the swagger property carrying `x-ms-notification-url: true`.
	if (operation.connectorId && operation.type === "ApiConnectionWebhook") {
		const defaultConnectionRef = findMatchingConnectionReference(
			operation.connectorId,
			connections,
			connectionReferences,
		);
		applyHostSerialization(inputs, serializeHost({
			connectionType: 'ApiConnectionWebhook',
			connectionKey: defaultConnectionRef ?? '',
			apiId: operation.connectorId,
			operationId: extractOperationId(operation),
		}));
	}

	// If this is a Service Provider operation, set up serviceProviderConfiguration
	if (operation.type === "ServiceProvider") {
		const serviceProviderId = extractServiceProviderIdForInputs(operation);
		const opId = extractServiceProviderOperationId(operation);

		if (serviceProviderId && opId) {
			const defaultConnectionRef = findMatchingConnectionReference(
				serviceProviderId,
				connections,
				connectionReferences,
			);
			applyHostSerialization(inputs, serializeHost({
				connectionType: 'ServiceProvider',
				connectionKey: defaultConnectionRef ?? '',
				serviceProviderId,
				operationId: opId,
			}));
		}

		// ServiceProvider operations follow the manifest convention
		// inputsLocation: ['inputs', 'parameters'] — operation parameters
		// (e.g. deploymentId, input) must be nested under inputs.parameters at the
		// runtime layer. Initialize an empty parameters object so subsequent
		// deserialization marks _hasParametersWrapper and serialization wraps
		// user-edited fields correctly. Without this, new ServiceProvider nodes
		// would emit parameters at the top level of inputs and fail at runtime.
		if (!inputs.parameters || typeof inputs.parameters !== 'object') {
			inputs.parameters = {};
		}
	}

	// If this is a Workflow operation, ensure proper host.workflow.id structure
	if (operation.type === "Workflow") {
		if (!inputs.host) {
			applyHostSerialization(inputs, serializeHost({
				connectionType: 'Workflow',
				connectionKey: '',
			}));
		}
	}

	// MCP connections
	if (operation.type === "MCP") {
		const defaultConnectionRef = findMatchingConnectionReference(
			operation.connectorId ?? '',
			connections,
			connectionReferences,
		);
		applyHostSerialization(inputs, serializeHost({
			connectionType: 'MCP',
			connectionKey: defaultConnectionRef ?? '',
		}));
	}

	// Agent operations: auto-select the first existing agent (model) connection
	// reference so a newly added Agent node is immediately runnable. Agent
	// connections are stored under inputs.modelConfigurations.model1.referenceName
	// rather than the host.connection structure used by other connector types.
	if (operation.type === "Agent") {
		const agentDefaults = (defaultInputs && typeof defaultInputs === 'object' && !Array.isArray(defaultInputs))
			? defaultInputs as Record<string, unknown>
			: {};
		// agentModelType may arrive at the top level of defaultInputs (legacy
		// callers) or nested under `parameters` (canonical runtime shape). Read
		// either, then canonicalize so the saved action always has
		// inputs.parameters.agentModelType and no top-level copy — useWorkflowGraph
		// reads the canonical location to pick the Foundry vs native node accent.
		const topLevelModelType = typeof agentDefaults.agentModelType === 'string'
			? (agentDefaults.agentModelType as string)
			: undefined;
		const nestedDefaultParams = (agentDefaults.parameters && typeof agentDefaults.parameters === 'object'
			? agentDefaults.parameters as Record<string, unknown>
			: {});
		const nestedModelType = typeof nestedDefaultParams.agentModelType === 'string'
			? (nestedDefaultParams.agentModelType as string)
			: undefined;
		const modelType = nestedModelType ?? topLevelModelType;
		const preferredProvider = modelType ?? 'AzureOpenAI';
		if (modelType) {
			const existingParams = (inputs.parameters && typeof inputs.parameters === 'object'
				? inputs.parameters as Record<string, unknown>
				: {});
			inputs.parameters = { ...existingParams, agentModelType: modelType };
		}
		delete (inputs as Record<string, unknown>).agentModelType;
		const lastUsed = getLastUsedAgentConnection();
		// Derive the agent variant from the defaults so auto-select only binds a
		// connection whose provider is valid for this variant. Workflow (native)
		// agents must never auto-bind a GitHub Copilot connection (GHCP is
		// coding-only), which keeps this consistent with the connection picker.
		const harnessType = readAgentHarnessType({}, agentDefaults);
		const agentVariant = getAgentVariantFromTypes(modelType, harnessType);
		const matcherOptions: FindAgentConnectionOptions = {
			preferredProvider,
			allowedProviders: getAllowedAgentProviders(agentVariant),
		};
		if (lastUsed?.key) matcherOptions.lastUsedKey = lastUsed.key;
		if (lastUsed?.connectionId) matcherOptions.lastUsedConnectionId = lastUsed.connectionId;
		const defaultAgentRef = findMatchingAgentConnectionReference(connections, connectionReferences, matcherOptions);
		if (defaultAgentRef) {
			const existingModelConfigs = (inputs.modelConfigurations as Record<string, Record<string, unknown>> | undefined) ?? {};
			const existingModel1 = existingModelConfigs.model1 ?? {};
			if (!existingModel1.referenceName) {
				inputs.modelConfigurations = {
					...existingModelConfigs,
					model1: { ...existingModel1, referenceName: defaultAgentRef },
				};
			}
		}
	}

	return inputs;
};

/**
 * Extract service provider ID for building inputs
 * Following LogicAppsUX pattern - the serviceProviderId is typically the connectorId
 * Format: /serviceProviders/{providerName}
 */
const extractServiceProviderIdForInputs = (operation: OperationDefinition): string | undefined => {
	// Primary: Use connectorId if it's a service provider path
	// This is set from op.properties.api.id in discoveryOperationToDefinition
	if (operation.connectorId && isServiceProviderConnector(operation.connectorId)) {
		return operation.connectorId;
	}

	// Try to extract from the full operation ID path (less common)
	if (operation.id) {
		const match = operation.id.match(/(\/serviceProviders\/[^/]+)/i);
		if (match) return match[1];
	}

	// Construct from kind if available (fallback)
	if (operation.kind) {
		return `/serviceProviders/${operation.kind}`;
	}

	return undefined;
};

/**
 * Extract operation ID for Service Provider operations
 * Following LogicAppsUX pattern - extracts the last segment of the operation ID
 * The operation.id from discovery API is typically just the operation name (e.g., 'createBlob')
 * or a full path (e.g., '/serviceProviders/AzureBlob/operations/createBlob')
 */
const extractServiceProviderOperationId = (operation: OperationDefinition): string | undefined => {
	if (!operation.id) return undefined;

	// If it's a full path, extract just the operation name (last segment)
	// Following LogicAppsUX: operationName = operationId.split('/').slice(-1)[0]
	if (operation.id.includes('/')) {
		const segments = operation.id.split('/').filter(Boolean);
		return segments[segments.length - 1];
	}

	// If it's already just the operation name, use it directly
	return operation.id;
};

/**
 * Markers that locate the operation-id tail inside a discovery `op.id`.
 * The capture group is everything after the marker; we then take the
 * last non-empty path segment so malformed search-API ids like
 * `…/apiOperations/{project}//{opName}` still resolve to `{opName}`.
 */
const OPERATION_ID_TAIL_MARKERS: readonly RegExp[] = [
	/\/apiOperations\/(.+)$/i,
	/\/serviceProviders\/[^/]+\/operations\/(.+)$/i,
];

/**
 * Extract operationId from a discovery operation.
 * Handles Azure managed connectors (`…/apiOperations/{opId}`) and Service
 * Provider operations (`/serviceProviders/{p}/operations/{opId}`). Falls
 * back to the raw id for shapes that don't match a known marker.
 */
export const extractOperationId = (operation: OperationDefinition): string | undefined => {
	if (!operation.id || !operation.isConnector) return undefined;
	for (const marker of OPERATION_ID_TAIL_MARKERS) {
		const tail = operation.id.match(marker)?.[1];
		if (!tail) continue;
		const segments = tail.split('/').filter(Boolean);
		if (segments.length > 0) return segments[segments.length - 1];
	}
	return operation.id;
};

/**
 * Extract the service provider ID from a Service Provider operation
 * Returns the full serviceProviderId path (e.g., "/serviceProviders/AzureBlob")
 */
export const extractServiceProviderId = (operation: OperationDefinition): string | undefined => {
	if (!operation.id || operation.type !== 'ServiceProvider') return undefined;

	// Extract /serviceProviders/{provider} from the full path
	const match = operation.id.match(/(\/serviceProviders\/[^/]+)/i);
	if (match) return match[1];

	// If connectorId is provided, use that
	if (operation.connectorId && isServiceProviderConnector(operation.connectorId)) {
		return operation.connectorId;
	}

	// Construct from kind if available
	if (operation.kind) {
		return `/serviceProviders/${operation.kind}`;
	}

	return undefined;
};

/**
 * Notification trigger inputs structure (from swagger)
 */
export interface NotificationTriggerInputs {
	fetch: {
		method: string;
		pathTemplate: { template: string };
		queries?: Record<string, unknown>;
	};
	subscribe: {
		method: string;
		pathTemplate: { template: string };
		body: Record<string, unknown>;
		queries?: Record<string, unknown>;
	};
}

/**
 * Create a Trigger from an OperationDefinition
 *
 * If connectionReferences is provided and a matching connection exists,
 * the connection is automatically populated.
 *
 * For ApiConnectionNotification triggers, notificationInputs provides the
 * swagger-based fetch/subscribe structure with proper paths and parameters.
 */
export const createTriggerFromOperation = (
	operation: OperationDefinition,
	position: Position,
	connections: AvailableConnection[],
	connectionReferences?: ConnectionReferences,
	notificationInputs?: NotificationTriggerInputs,
): Trigger => {
	// For notification triggers with swagger-based inputs, use those instead of building from scratch
	let inputs: ActionInputs;
	if (operation.type === "ApiConnectionNotification" && notificationInputs) {
		// Use swagger-based notification inputs but route the host shape through
		// the centralized serializer so we get host.apiId / host.operationId in
		// addition to host.connection.referenceName. Without apiId here,
		// useNodeOperationInfo can't resolve the connector ID before the user
		// has finished creating the connection, and the ConnectionCreationPanel
		// fails with "Missing connector ID". See workflowHelpers.ts:170-180
		// (the non-swagger path) for the symmetric serialization call.
		const defaultConnectionRef = findMatchingConnectionReference(
			operation.connectorId ?? "",
			connections,
			connectionReferences,
		);
		inputs = {
			fetch: notificationInputs.fetch,
			subscribe: notificationInputs.subscribe,
		};

		applyHostSerialization(inputs, serializeHost({
			connectionType: 'ApiConnectionNotification',
			connectionKey: defaultConnectionRef ?? '',
			apiId: operation.connectorId,
			operationId: extractOperationId(operation),
		}));
	} else {
		// Use standard input building
		inputs = buildConnectorInputs(operation, connections, connectionReferences);
	}

	const trigger: Trigger = {
		type: operation.type,
		...(operation.kind ? { kind: operation.kind } : {}),
		inputs,
		metadata: {
			position,
		},
	};

	// Note: operationId is NOT added to the trigger object.
	// Like LogicAppsUX, we store operationId separately in parameterValues as '_operationId'
	// for internal use (parameter lookup, swagger fetching). It should not be persisted
	// in the workflow definition as Azure's API doesn't recognize it.

	return trigger;
};

/**
 * Snap a position to the grid (10 pixel intervals)
 */
export const snapPositionToGrid = (position: Position): Position => {
	return {
		x: Math.round(position.x / SNAP_GRID[0]) * SNAP_GRID[0],
		y: Math.round(position.y / SNAP_GRID[1]) * SNAP_GRID[1],
	};
};

/**
 * Calculate phantom node position for terminal edges
 * Ensures consistent positioning based on node dimensions and layout orientation
 * Positions phantom node so its handle EXACTLY matches the source node handle for perfectly straight edges
 * Rounds handle positions to whole pixels to avoid sub-pixel rendering artifacts
 * When handleIndex/handleCount are provided, offsets perpendicular to flow to avoid overlap
 */
export const calculatePhantomNodePosition = (
	nodePosition: Position,
	nodeWidth: number,
	nodeHeight: number,
	isHorizontal: boolean,
	edgeLength: number = 80,
	handleIndex: number = 0,
	handleCount: number = 1,
): Position => {
	const PHANTOM_SIZE = 1; // Phantom node is 1x1
	// Spread multiple handles apart perpendicular to flow direction
	const HANDLE_SPREAD = 60;
	const spreadOffset = handleCount > 1
		? (handleIndex - (handleCount - 1) / 2) * HANDLE_SPREAD
		: 0;

	if (isHorizontal) {
		// Horizontal layout: extend to the right
		// Source handle is at nodeY + nodeHeight/2
		// Round to whole pixel to avoid sub-pixel rendering issues
		const sourceHandleY = Math.round(nodePosition.y + nodeHeight / 2);
		// Phantom handle is at phantomY + PHANTOM_SIZE/2, so solve for phantomY
		const phantomY = sourceHandleY - PHANTOM_SIZE / 2 + spreadOffset;

		return {
			x: nodePosition.x + nodeWidth + edgeLength,
			y: phantomY,
		};
	}

	// Vertical layout: extend downward
	// Source handle is at nodeX + nodeWidth/2
	// Round to whole pixel to avoid sub-pixel rendering issues
	const sourceHandleX = Math.round(nodePosition.x + nodeWidth / 2);
	// Phantom handle is at phantomX + PHANTOM_SIZE/2, so solve for phantomX
	const phantomX = sourceHandleX - PHANTOM_SIZE / 2 + spreadOffset;

	return {
		x: phantomX,
		y: nodePosition.y + nodeHeight + edgeLength,
	};
};

// Perpendicular offset for scoped insertions (loop body, if branches, etc.)
// Matches the layout engine's BRANCH_LANE_SPACING so the node appears in the correct lane
export const SCOPED_LANE_OFFSET = 110;

/**
 * Count how many existing actions run directly after the same source.
 * These are "parallel siblings" — actions that share the same runAfter source
 * with the new action being added. Used to offset parallel branch positions.
 *
 * @param actions - The top-level actions record
 * @param sourceActionName - The action name (not node ID) that will be in runAfter
 * @param isTriggerSource - Whether the source is the trigger (empty runAfter)
 */
export const countParallelSiblings = (
	actions: Record<string, Action>,
	sourceActionName: string,
	isTriggerSource: boolean,
): number => {
	let count = 0;
	for (const action of Object.values(actions)) {
		const runAfter = action.runAfter ?? {};
		if (isTriggerSource) {
			// Actions with empty runAfter run after the trigger
			if (Object.keys(runAfter).length === 0 && !action.detached) {
				count++;
			}
		} else {
			// Actions that have this source in their runAfter
			if (sourceActionName in runAfter) {
				count++;
			}
		}
	}
	return count;
};

/**
 * Calculate the position for a new node being inserted.
 *
 * When a `targetPosition` is provided (i.e. inserting *between* source and
 * target), the new node lands one full slot past the source along the flow
 * axis — the same step the auto-layout reserves
 * (`DEFAULT_NODE_WIDTH/HEIGHT + NODE_SPACING`). The store's `addAction`
 * follows this by translating every transitive descendant of the target by
 * the same slot so spacing stays uniform end-to-end.
 *
 * When `targetPosition` is undefined (end-of-chain insert), behaviour is
 * unchanged: the new node is appended after the source.
 *
 * Falls back to a midpoint placement only when the gap between source and
 * target is already wide enough to fit a full slot on each side — in that
 * case the existing layout already has room, no push is needed, and a
 * midpoint reads more naturally to the eye.
 *
 * When `existingParallelSiblingCount` is provided (> 0), the new node is
 * offset along the cross-axis to avoid overlapping with existing parallel
 * siblings. This ensures new parallel branches don't land directly on top
 * of existing actions that run after the same source.
 */
export const calculateInsertionPosition = (
	sourcePosition: Position,
	targetPosition: Position | undefined,
	isHorizontal: boolean,
	scope?: { parentActionName: string; branchType: string },
	sourceNodeSize?: { width?: number; height?: number },
	minimumNodeWidth: number = 0,
	existingParallelSiblingCount: number = 0,
): Position => {
	const laneOffset = scope ? getScopeLaneOffset(scope.branchType) * SCOPED_LANE_OFFSET : 0;
	const measuredNodeWidth = sourceNodeSize?.width ?? DEFAULT_NODE_WIDTH;
	const nodeWidth = Math.max(measuredNodeWidth, minimumNodeWidth);
	const nodeHeight = sourceNodeSize?.height ?? DEFAULT_NODE_HEIGHT;

	if (targetPosition) {
		// Decide between "slot placement" (new node one slot past source,
		// then push downstream) and "midpoint" (keep the existing wide gap).
		// The slot stride matches what `layoutPushDownstream` uses (always the
		// layout defaults, never measured sizes), so spacing stays uniform
		// regardless of which branch we take and the store's "is this slot
		// placement?" detector can compare offsets reliably.
		//
		// Use the absolute gap so an unusual target-left-of-source layout
		// still picks the right branch instead of always falling into slot
		// placement.
		const slotStride = isHorizontal
			? DEFAULT_NODE_WIDTH + NODE_SPACING
			: DEFAULT_NODE_HEIGHT + NODE_SPACING;
		const gap = Math.abs(
			isHorizontal
				? targetPosition.x - sourcePosition.x
				: targetPosition.y - sourcePosition.y,
		);
		const fitsMidpoint = gap >= 2 * slotStride;
		if (fitsMidpoint) {
			return {
				x: (sourcePosition.x + targetPosition.x) / 2,
				y: (sourcePosition.y + targetPosition.y) / 2,
			};
		}
		// Slot placement. Y/X stays on the source's lane so the new node
		// aligns with the rest of the chain. Direction follows the sign of
		// the source→target delta so a target-left-of-source insert still
		// lands the new node between them rather than past the source.
		const direction = isHorizontal
			? Math.sign(targetPosition.x - sourcePosition.x) || 1
			: Math.sign(targetPosition.y - sourcePosition.y) || 1;
		if (isHorizontal) {
			return {
				x: sourcePosition.x + direction * slotStride,
				y: sourcePosition.y,
			};
		}
		return {
			x: sourcePosition.x,
			y: sourcePosition.y + direction * slotStride,
		};
	}

	// Add after source (at the end or as a parallel branch)
	// When adding as a parallel branch (no targetPosition, no scope), offset
	// by the number of existing parallel siblings to avoid overlap.
	const parallelOffset = !targetPosition && !scope && existingParallelSiblingCount > 0
		? existingParallelSiblingCount * SCOPED_LANE_OFFSET
		: 0;

	if (isHorizontal) {
		return {
			x: sourcePosition.x + nodeWidth + NODE_SPACING,
			y: sourcePosition.y + laneOffset + parallelOffset,
		};
	}
	return {
		x: sourcePosition.x + laneOffset + parallelOffset,
		y: sourcePosition.y + nodeHeight + NODE_SPACING,
	};
};

/**
 * Returns true if a terminal handle on a control-flow node represents a nested-scope
 * branch endpoint (if true/false, foreach loop body, switch case-X/default).
 * These terminals get their phantom placed at the natural new-node landing spot
 * inside the branch lane, instead of along the node's primary flow axis.
 */
export const isScopedBranchTerminal = (
	nodeType: string | undefined,
	handle: string,
): boolean => {
	if (nodeType === 'foreach' && handle === 'loop') return true;
	if (nodeType === 'if' && (handle === 'true' || handle === 'false')) return true;
	if (nodeType === 'switch' && (handle.startsWith('case-') || handle === 'default')) return true;
	return false;
};

/**
 * Compute the phantom node position for a terminal "+" target.
 *
 * For scoped branch terminals (If true/false, ForEach loop, Switch
 * case-X/default), the phantom is placed close to the source node — just past
 * its bounding box along the flow axis, with a small perpendicular branch
 * offset — so the visible "+" sits compactly next to the parent (like the
 * regular runAfter terminal). The new action still lands at its natural
 * `calculateInsertionPosition` spot when the "+" is clicked.
 *
 * For other terminals, falls back to the perpendicular-spread default.
 */
export const computeTerminalPhantomPosition = (
	sourceNodePosition: Position,
	sourceNodeWidth: number,
	sourceNodeHeight: number,
	isHorizontal: boolean,
	handle: string,
	nodeType: string | undefined,
	actionName: string,
	handleIndex: number = 0,
	handleCount: number = 1,
): Position => {
	if (isScopedBranchTerminal(nodeType, handle)) {
		const EDGE_LENGTH = 80;
		const PHANTOM_SIZE = 1;
		// Distance from the source node's bbox edge to the phantom along the
		// cross axis. Anchoring to the *edge* (rather than the handle) keeps
		// True and False phantoms mirror-symmetric about the diamond center,
		// regardless of whether each handle sits exactly on the bbox corner.
		const BRANCH_PERP_FROM_BBOX_EDGE = 60;
		// Suppress unused-arg warnings while preserving the public signature.
		void actionName; void handleIndex; void handleCount;

		// Symmetric placement around the *diamond center*. The bbox height
		// includes the label text below the diamond, so anchoring to the
		// bbox edges makes False sit further away than True. The diamond
		// itself is 56px tall and sits at the top of the bbox, so its
		// center is always at sourceNodePosition.y + 28 (independent of
		// label length). We offset by half-diamond + BRANCH_PERP so the
		// distance from the diamond edge stays the same on both sides.
		const DIAMOND_SIZE = 56;
		const halfDiamond = DIAMOND_SIZE / 2;
		const direction = getScopeLaneOffset(handle); // -1 for true/loop, +1 for false/done
		if (isHorizontal) {
			const diamondCenterY = sourceNodePosition.y + halfDiamond;
			const phantomY = diamondCenterY + direction * (halfDiamond + BRANCH_PERP_FROM_BBOX_EDGE);
			return {
				x: sourceNodePosition.x + sourceNodeWidth + EDGE_LENGTH,
				y: Math.round(phantomY) - PHANTOM_SIZE / 2,
			};
		}
		const diamondCenterX = sourceNodePosition.x + halfDiamond;
		const phantomX = diamondCenterX + direction * (halfDiamond + BRANCH_PERP_FROM_BBOX_EDGE);
		return {
			x: Math.round(phantomX) - PHANTOM_SIZE / 2,
			y: sourceNodePosition.y + sourceNodeHeight + EDGE_LENGTH,
		};
	}
	return calculatePhantomNodePosition(
		sourceNodePosition,
		sourceNodeWidth,
		sourceNodeHeight,
		isHorizontal,
		80,
		handleIndex,
		handleCount,
	);
};

/** Maps branch types to lane offsets matching the layout engine's handleToLane logic */
const getScopeLaneOffset = (branchType: string): number => {
	if (branchType === 'true' || branchType === 'loop') return -1;
	if (branchType === 'false' || branchType === 'done' || branchType === 'default') return 1;
	if (branchType.startsWith('case-')) return -1;
	return 0;
};

/**
 * Minimal terminal-handle descriptor needed for toolbox-drop hit-testing.
 * The component owns a richer shape; this is the structural subset consumed here.
 */
export type ScopedTerminalHandle = {
	nodeId: string;
	handle: string;
	nodeType: string | undefined;
	actionName: string;
};

/** Minimal canvas-node shape used by the toolbox-drop hit-test (subset of `@xyflow/react` Node). */
export type ScopedHitTestNode = {
	id: string;
	position: Position;
	measured?: { width?: number; height?: number };
};

/** Result of locating a scoped-branch insertion point under a toolbox drop. */
export type ScopedBranchInsertion = {
	/** Where the action should be inserted (sourceNodeId + scope). */
	insertionPoint: {
		sourceNodeId: string;
		scope: { parentActionName: string; branchType: string };
	};
	/** Lane-snapped landing position to write into `action.metadata.position`. */
	snappedPosition: Position;
};

/**
 * Hit-test a toolbox drop position against scoped branch terminal phantoms
 * (If true/false, ForEach loop, Switch case/default).
 *
 * Each scoped terminal claims a generous half-plane of the area downstream of its
 * source node: in horizontal layout the true branch owns everything above the
 * node's centerline (out to past the phantom) and false everything below;
 * vertical layout splits left/right. This means dropping anywhere in the
 * visible "true branch lane" — even past the phantom or off-axis — reliably
 * nests into Condition.actions. Ties are broken by Euclidean distance to the
 * phantom (the visible "+" target).
 *
 * Returns `undefined` when no scoped terminal lane contains the drop.
 */
export const findScopedBranchInsertion = (
	dropPosition: Position,
	terminalHandles: ReadonlyArray<ScopedTerminalHandle>,
	allNodes: ReadonlyArray<ScopedHitTestNode>,
	isHorizontal: boolean,
): ScopedBranchInsertion | undefined => {
	const LANE_PAST_PHANTOM = DEFAULT_NODE_WIDTH; // extent past the phantom in flow direction
	const LANE_PAST_PERP = SCOPED_LANE_OFFSET + DEFAULT_NODE_HEIGHT; // outer perpendicular extent

	let best: ScopedBranchInsertion | undefined;
	let nearestDist = Infinity;

	for (const th of terminalHandles) {
		if (!isScopedBranchTerminal(th.nodeType, th.handle)) continue;

		const phantomId = `terminal-target-${th.nodeId}-${th.handle}`;
		const phantom = allNodes.find((n) => n.id === phantomId);
		const sourceNode = allNodes.find((n) => n.id === th.nodeId);
		if (!phantom || !sourceNode) continue;

		const nodeWidth = sourceNode.measured?.width ?? DEFAULT_NODE_WIDTH;
		const nodeHeight = sourceNode.measured?.height ?? DEFAULT_NODE_HEIGHT;
		const sourceCenterX = sourceNode.position.x + nodeWidth / 2;
		const sourceCenterY = sourceNode.position.y + nodeHeight / 2;

		// Compute the lane bbox: source-side edge to past-phantom in flow,
		// half-plane (per branch) plus a fixed outer cap perpendicular.
		let minX: number, maxX: number, minY: number, maxY: number;
		if (isHorizontal) {
			minX = sourceNode.position.x;
			maxX = phantom.position.x + LANE_PAST_PHANTOM;
			// True/loop = top half, false/default/done = bottom half, case-X = top half
			const isTopHalf =
				th.handle === 'true' || th.handle === 'loop' || th.handle.startsWith('case-');
			if (isTopHalf) {
				minY = sourceCenterY - LANE_PAST_PERP;
				maxY = sourceCenterY;
			} else {
				minY = sourceCenterY;
				maxY = sourceCenterY + LANE_PAST_PERP;
			}
		} else {
			minY = sourceNode.position.y;
			maxY = phantom.position.y + LANE_PAST_PHANTOM;
			const isLeftHalf =
				th.handle === 'true' || th.handle === 'loop' || th.handle.startsWith('case-');
			if (isLeftHalf) {
				minX = sourceCenterX - LANE_PAST_PERP;
				maxX = sourceCenterX;
			} else {
				minX = sourceCenterX;
				maxX = sourceCenterX + LANE_PAST_PERP;
			}
		}
		const inLane =
			dropPosition.x >= minX && dropPosition.x <= maxX &&
			dropPosition.y >= minY && dropPosition.y <= maxY;
		if (!inLane) continue;

		// Tie-break by distance from the phantom (the visible "+" target).
		const dx = phantom.position.x - dropPosition.x;
		const dy = phantom.position.y - dropPosition.y;
		const dist = Math.hypot(dx, dy);
		if (dist >= nearestDist) continue;
		nearestDist = dist;

		const snappedPosition = calculateInsertionPosition(
			sourceNode.position,
			undefined,
			isHorizontal,
			{ parentActionName: th.actionName, branchType: th.handle },
		);

		best = {
			insertionPoint: {
				sourceNodeId: th.nodeId,
				scope: { parentActionName: th.actionName, branchType: th.handle },
			},
			snappedPosition,
		};
	}

	return best;
};

/**
 * Calculate a position for a new floating node that doesn't overlap existing nodes.
 * Places the node to the right of (horizontal) or below (vertical) the existing workflow.
 */
export const calculateFloatingNodePosition = (
	existingPositions: Position[],
	isHorizontal: boolean,
): Position => {
	if (existingPositions.length === 0) {
		// No existing nodes - use default position
		return DEFAULT_TRIGGER_POSITION;
	}

	// Find the bounding box of existing nodes
	let maxX = -Infinity;
	let maxY = -Infinity;
	let minY = Infinity;
	let minX = Infinity;

	for (const pos of existingPositions) {
		maxX = Math.max(maxX, pos.x);
		maxY = Math.max(maxY, pos.y);
		minX = Math.min(minX, pos.x);
		minY = Math.min(minY, pos.y);
	}

	// Place the new node clearly outside the bounding box.
	// Use generous spacing so it doesn't overlap with existing nodes
	// or with where auto-layout might position connected nodes.
	const paddingX = DEFAULT_NODE_WIDTH + NODE_SPACING;
	const paddingY = DEFAULT_NODE_HEIGHT + NODE_SPACING;

	if (isHorizontal) {
		// Horizontal layout: place to the right of the rightmost node, aligned to middle row
		const midY = existingPositions.length > 1
			? Math.round((minY + maxY) / 2)
			: minY;
		return {
			x: maxX + paddingX,
			y: midY,
		};
	}

	// Vertical layout: place below the bottommost node, aligned to middle column
	const midX = existingPositions.length > 1
		? Math.round((minX + maxX) / 2)
		: minX;
	return {
		x: midX,
		y: maxY + paddingY,
	};
};

/**
 * Create an Action from an OperationDefinition
 * Handles special cases for control flow operations that need structured initialization
 *
 * If connectionReferences is provided and a matching connection exists,
 * the connection is automatically populated.
 */
export const createActionFromOperation = (
	operation: OperationDefinition,
	position: Position,
	connections: AvailableConnection[],
	connectionReferences?: ConnectionReferences,
): Action => {
	const baseAction = {
		type: operation.type,
		...(operation.kind ? { kind: operation.kind } : {}),
		metadata: {
			position,
			// Store swagger path template for path parameter round-trip
			...(operation.swaggerPathTemplate && { swaggerPathTemplate: operation.swaggerPathTemplate }),
		},
	};

	// Handle control flow operations that need special structure
	switch (operation.type) {
		case "If":
			return {
				...baseAction,
				expression: "",
				actions: {},
				else: { actions: {} },
			} as Action;

		case "Switch":
			return {
				...baseAction,
				expression: "",
				cases: {},
				default: { actions: {} },
			} as Action;

		case "Foreach":
			return {
				...baseAction,
				foreach: "",
				actions: {},
			} as Action;

		case "Until":
			return {
				...baseAction,
				expression: "",
				limit: {
					count: 60,
					timeout: "PT1H",
				},
				actions: {},
			} as Action;

		case "Scope":
			return {
				...baseAction,
				actions: {},
			} as Action;

		case "Agent": {
			const agentInputs = buildConnectorInputs(operation, connections, connectionReferences);
			return {
				...baseAction,
				inputs: agentInputs,
				limit: {
					count: 60,
					timeout: "PT1H",
				},
			} as Action;
		}

		default: {
			// For standard actions, use inputs from defaultInputs
			// For managed connectors (ApiConnection), set up host.apiId for swagger parameter lookup
			const inputs = buildConnectorInputs(operation, connections, connectionReferences);

			const action: Action = {
				...baseAction,
				inputs,
			};

			// Note: operationId is NOT added to the action object.
			// Like LogicAppsUX, we store operationId separately in parameterValues as '_operationId'
			// for internal use (parameter lookup, swagger fetching). It should not be persisted
			// in the workflow definition as Azure's API doesn't recognize it.

			return action;
		}
	}
};

/**
 * Checks if any triggers or actions in the workflow are missing position data.
 * Used to determine if auto-layout should be applied on load.
 *
 * Positions live in the client-side layout store (`workflowLayoutStore`)
 * keyed by React Flow node id. The optional `positions` map is the store's
 * snapshot; when present we use it as the authority. The legacy
 * `metadata.position` fallback covers calls made before the layout store is
 * populated (e.g., during initial sync where migration runs in a separate
 * effect tick) and the subworkflow preview which doesn't go through the
 * store at all.
 */
export const hasNodesWithoutPositions = (
	workflow: WorkflowDefinition,
	positions: Record<string, { x: number; y: number }> = {},
): boolean => {
	// Check triggers
	for (const [name, trigger] of Object.entries(workflow.definition.triggers ?? {})) {
		if (!positions[`trigger-${name}`] && !trigger.metadata?.position) return true;
	}

	// Check actions (including nested ones in If/Switch/Foreach)
	const checkActions = (actions: Record<string, Action>, scopePrefix = ''): boolean => {
		for (const [name, action] of Object.entries(actions)) {
			const scopedId = `${scopePrefix}${name}`;
			if (!positions[`action-${scopedId}`] && !action.metadata?.position) return true;

			// Check nested actions in control flow nodes
			if (isIfAction(action)) {
				if (action.actions && checkActions(action.actions, `${scopedId}/`)) return true;
				if (action.else?.actions && checkActions(action.else.actions, `${scopedId}/`))
					return true;
			}
			if (isSwitchAction(action)) {
				for (const caseData of Object.values(action.cases)) {
					if (
						caseData &&
						typeof caseData === "object" &&
						"actions" in caseData &&
						caseData.actions
					) {
						if (checkActions(caseData.actions as Record<string, Action>, `${scopedId}/`))
							return true;
					}
				}
				if (
					action.default?.actions &&
					checkActions(action.default.actions as Record<string, Action>, `${scopedId}/`)
				)
					return true;
			}
			if (isForeachAction(action)) {
				if (action.actions && checkActions(action.actions, `${scopedId}/`)) return true;
			}
		}
		return false;
	};

	return checkActions(workflow.definition.actions ?? {});
};

/**
 * Determine the correct source handle for a terminal edge based on node type
 */
export const getTerminalEdgeSourceHandle = (
	nodeType: string | undefined,
): string | null => {
	if (nodeType === 'action' || nodeType === 'trigger' || nodeType === 'agent') {
		return 'output';
	} else if (nodeType === 'foreach') {
		return 'done';
	} else if (nodeType === 'if') {
		return 'merge';
	} else if (nodeType === 'switch') {
		return 'default';
	}
	return null;
};

/**
 * Returns all output handle IDs for a given node type.
 * Control flow nodes have multiple output handles (one per branch).
 */
export const getNodeOutputHandles = (
	nodeType: string | undefined,
	cases?: string[],
): string[] => {
	switch (nodeType) {
		case 'foreach':
			return ['loop', 'done'];
		case 'if':
			return ['true', 'false', 'merge'];
		case 'switch':
			return [...(cases ?? []).map((c) => `case-${c}`), 'default'];
		default:
			return ['output'];
	}
};

/**
 * Compute the number of graph edges that the workflow definition implies.
 *
 * Used to distinguish a legitimate zero-edge workflow (every top-level action
 * detached, no runAfter dependencies, no scope children) from a transient
 * empty-edges render where the graph has not finished recomputing yet.
 * Mirrors the edge-derivation rules in useWorkflowGraph:
 *   - trigger → root-action edge for each (trigger × non-detached top-level
 *     action with empty runAfter); useWorkflowGraph emits one edge per pair
 *   - one edge per key in any action's runAfter
 *   - branch edge from a scope parent to each first-action child (no runAfter)
 */
export const countExpectedEdges = (workflow: WorkflowDefinition | null): number => {
	if (!workflow?.definition) return 0;
	const triggerCount = Object.keys(workflow.definition.triggers ?? {}).length;
	let count = 0;

	const visit = (actions: Record<string, Action>, isTopLevel: boolean) => {
		for (const action of Object.values(actions)) {
			const runAfterKeys = Object.keys(action.runAfter ?? {});
			count += runAfterKeys.length;

			if (isTopLevel) {
				// One trigger → action edge per trigger for non-detached root actions
				// with no runAfter dependencies.
				if (triggerCount > 0 && runAfterKeys.length === 0 && !action.detached) {
					count += triggerCount;
				}
			} else if (runAfterKeys.length === 0) {
				// Branch edge from scope parent to its first action
				count += 1;
			}

			// Recurse into scope children
			if (isIfAction(action)) {
				const ia = action as Action & { actions?: Record<string, Action>; else?: { actions?: Record<string, Action> } };
				if (ia.actions) visit(ia.actions, false);
				if (ia.else?.actions) visit(ia.else.actions, false);
			} else if (isSwitchAction(action)) {
				const sa = action as Action & {
					cases?: Record<string, { case: string | number; actions?: Record<string, Action> }>;
					default?: { actions?: Record<string, Action> };
				};
				const cases = sa.cases ?? {};
				for (const caseName of Object.keys(cases)) {
					const caseDef = cases[caseName];
					if (caseDef?.actions) visit(caseDef.actions, false);
				}
				if (sa.default?.actions) visit(sa.default.actions, false);
			} else if (isForeachAction(action) || isUntilAction(action)) {
				const loop = action as Action & { actions?: Record<string, Action> };
				if (loop.actions) visit(loop.actions, false);
			} else if (isAgentAction(action)) {
				const ag = action as Action & { tools?: Record<string, { actions?: Record<string, Action> }> };
				const tools = ag.tools ?? {};
				for (const toolName of Object.keys(tools)) {
					const tool = tools[toolName];
					if (tool?.actions) visit(tool.actions, false);
				}
			}
		}
	};

	visit(workflow.definition.actions ?? {}, true);
	return count;
};
